// src/components/AuthModal.jsx
import { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import { getRememberedEmail } from '../utils/tokenUtils';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';
import { Eye, EyeOff } from "lucide-react";
import ForgotPassword from '../pages/ForgotPassword.jsx';
import { useLocation } from 'react-router-dom';
import { User, ChevronDown, X, } from 'lucide-react';

const AuthModal = ({ onClose }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const autoOtp = params.get('otp');
  const autoEmail = params.get('email');
  const [showForgotPassword, setShowForgotPassword] = useState(() => autoOtp && autoEmail ? true : false);

  const navigate = useNavigate();


  const handleClose = () => {
    // Remove query params and go to plain /auth (or landing/home as needed)
    navigate('/', { replace: true });
    if (onClose) onClose();
  };


  useEffect(() => {
    if (autoOtp && autoEmail) {
      setShowForgotPassword(true);
    }
  }, [autoOtp, autoEmail]);





  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
      <div className="bg-[#181818] rounded-xl border border-[#2b2b2b] w-full max-w-md relative overflow-hidden">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white"
        >
          <X size={24} />
        </button>

        {/* Tabs */}
        {!showForgotPassword && (
          <div className="flex border-b border-[#2b2b2b]">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-4 font-medium ${isLogin ? 'text-lime-400 border-b-2 border-lime-400' : 'text-gray-400'}`}
            >
              Login
            </button>
            {/* <button
            onClick={() => setIsLogin(false)}
            className={`flex-1 py-4 font-medium ${!isLogin ? 'text-lime-400 border-b-2 border-lime-400' : 'text-gray-400'}`}
          >
            Sign Up
          </button> */}
          </div>
        )}

        {/* Form Container */}
        <div className="p-6">
          {showForgotPassword ? (
            <ForgotPassword onClose={onClose} onBack={() => setShowForgotPassword(false)}
              autoEmail={autoEmail}
              autoOtp={autoOtp}
            />
          ) : isLogin ? (
            <LoginForm
              onClose={onClose}
              setMessage={setMessage}
              message={message}
              onForgotPassword={() => setShowForgotPassword(true)}
            />
          ) : (
            <SignupForm setIsLogin={setIsLogin} setMessage={setMessage} message={message} />
          )}
        </div>
      </div>
    </div>





  );
};


const LoginForm = ({ onClose, setMessage, message, onForgotPassword }) => {
  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
    otp: ''
  });
  const [loginMode, setLoginMode] = useState('password');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [otpSent, setOtpSent] = useState(false);
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [isResendDisabled, setIsResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [timerId, setTimerId] = useState(null);
  const [rememberMe, setRememberMe] = useState(false); // Remember Me state
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const phoneInputRef = useRef(null);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (timerId) clearInterval(timerId);
    };
  }, [timerId]);

  // Load remembered email on mount
  useEffect(() => {
    const rememberedEmail = getRememberedEmail();
    if (rememberedEmail) {
      setFormData(prev => ({
        ...prev,
        identifier: rememberedEmail
      }));
      setRememberMe(true);
    }
  }, []);




  const validate = () => {
    const newErrors = {};

    if (loginMode === 'password') {
      if (!formData.identifier.trim()) {
        newErrors.identifier = 'Email or phone is required';
      }
      if (!formData.password) {
        newErrors.password = 'Password is required';
      }
    } else {
      if (!otpSent) {
        if (!formData.identifier.trim()) {
          newErrors.identifier = 'Phone number is required';
        } else if (!/^[6-9]\d{9}$/.test(formData.identifier)) {
          newErrors.identifier = 'Enter a valid 10-digit phone number';
        }
      } else {
        if (!formData.otp) {
          newErrors.otp = 'OTP is required';
        } else if (formData.otp.length !== 6) {
          newErrors.otp = 'OTP must be 6 digits';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const startCountdown = () => {
    if (timerId) {
      clearInterval(timerId);
    }
    setCountdown(24);
    setIsResendDisabled(true);

    const id = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          setIsResendDisabled(false);
          setCountdown(0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    setTimerId(id);
  };

  const handleSendOtp = async () => {
    if (isResendDisabled && countdown > 0) return;
    setIsResendDisabled(true);
    setMessage('');
    setErrors(prev => ({ ...prev, identifier: '', otp: '' }));
    setFormData(prev => ({ ...prev, otp: '' }));

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: formData.identifier })
      });
      const data = await res.json();
      if (res.ok) {
        setOtpSent(true);
        startCountdown();
        setMessage({ type: 'success', text: 'OTP sent successfully' });
      } else {
        setIsResendDisabled(false);
        setErrors(prev => ({ ...prev, identifier: data.message || 'Failed to send OTP' }));
      }
    } catch {
      setIsResendDisabled(false);
      setErrors(prev => ({ ...prev, identifier: 'Network error' }));
    }
  };

  const handleChangePhone = () => {
    setOtpSent(false);
    setFormData(prev => ({ ...prev, otp: '' }));
    setErrors({});
    setMessage('');
    if (timerId) {
      clearInterval(timerId);
      setTimerId(null);
    }
    setCountdown(0);
    setIsResendDisabled(false);

    // Focus phone input and place cursor at end
    setTimeout(() => {
      if (phoneInputRef.current) {
        phoneInputRef.current.focus();
        const len = phoneInputRef.current.value.length;
        phoneInputRef.current.setSelectionRange(len, len);
      }
    }, 50);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    
    if (loginMode === 'otp' && !otpSent) {
      if (/^[6-9]\d{9}$/.test(formData.identifier)) {
        await handleSendOtp();
      } else {
        setErrors({ identifier: 'Enter a valid 10-digit phone number' });
      }
      return;
    }

    if (!validate()) return;

    try {
      const endpoint = loginMode === 'password'
        ? `${API_BASE_URL}/api/auth/login`
        : `${API_BASE_URL}/api/auth/login-with-otp`;

      const payload = loginMode === 'password'
        ? { identifier: formData.identifier, password: formData.password }
        : { identifier: formData.identifier, otp: formData.otp };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (res.ok) {
        if (rememberMe && formData.identifier) {
          localStorage.setItem('rememberedEmail', formData.identifier);
        }
        login(data, rememberMe);
        const role = data.user?.role || 'User';
        const dashboardMap = {
          'Owner': '/Odashboard',
          'Trainer': '/edashboard',
          'User': '/customerdashboard'
        };
        setIsLoggingIn(true);
        navigate(dashboardMap[role] || '/customerdashboard', { replace: true });
        onClose?.();
      } else {
        if (loginMode === 'otp') {
          setFormData(prev => ({ ...prev, otp: '' }));
          setErrors({ otp: data.message || 'Login failed' });
        } else {
          setMessage({ type: 'error', text: data.message || 'Login failed' });
        }
      }
    } catch {
      if (loginMode === 'otp') {
        setErrors({ otp: 'Network error' });
      } else {
        setMessage({ type: 'error', text: 'Network error' });
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatPhoneNumber = (phone) => {
    if (phone && phone.length === 10) {
      return `${phone.slice(0, 5)} ${phone.slice(5)}`;
    }
    return phone;
  };


  if (isLoggingIn) {
    return (
      <div className="flex justify-center items-center h-screen bg-[#101010]">
        <div className="animate-spin rounded-full h-10 w-10 
                        border-b-2 border-[#6CD800]" />
      </div>
    );
  }

  const isPhoneValid = /^[6-9]\d{9}$/.test(formData.identifier);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Mode Switch */}
      <div className="flex space-x-2 mb-2">
        <button
          type="button"
          className={`flex-1 py-2 rounded-lg ${loginMode === 'password' ? 'bg-lime-400 text-black' : 'bg-[#333] text-white'}`}
          onClick={() => setLoginMode('password')}
        >
          Password Login
        </button>
        <button
          type="button"
          className={`flex-1 py-2 rounded-lg ${loginMode === 'otp' ? 'bg-lime-400 text-black' : 'bg-[#333] text-white'}`}
          onClick={() => setLoginMode('otp')}
        >
          OTP Login
        </button>
      </div>

      {loginMode === 'password' ? (
        <>
          {/* Email/Phone input */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Email / Phone Number
            </label>
            <input
              type="text"
              value={formData.identifier}
              onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
              placeholder="Enter email or phone number"
              className="w-full px-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg text-white"
              required
            />
            {errors.identifier && (
              <p className="mt-1 text-xs text-red-400">{errors.identifier}</p>
            )}
          </div>

          {/* Password field */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full px-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg text-white pr-10"
              required
            />

            {errors.password && (
              <p className="mt-1 text-xs text-red-400">{errors.password}</p>
            )}

            {formData.password && (
              <button
                type="button"
                className="absolute right-3 top-[65%] transform -translate-y-1/2 text-gray-400"
                onClick={() => setShowPassword((show) => !show)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            )}
          </div>

          {/* Remember Me + Forgot Password */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 text-lime-400 bg-[#222] border-[#2b2b2b] rounded focus:ring-lime-400 cursor-pointer"
              />
              <label htmlFor="rememberMe" className="text-sm text-gray-400 cursor-pointer select-none">
                Remember me
              </label>
            </div>

            <button
              type="button"
              onClick={onForgotPassword}
              className="text-sm text-lime-400 hover:underline"
            >
              Forgot password?
            </button>
          </div>

          {/* Submit */}
          <button
            type="submit"
            className="w-full bg-lime-400 hover:bg-lime-300 text-[#181f10] font-semibold py-2 px-4 rounded-lg"
          >
            Sign in
          </button>
        </>
      ) : (
        /* OTP Login Mode */
        <>
          {!otpSent ? (
            /* Step 1 — Phone number entry */
            <>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Phone Number
                </label>
                <input
                  ref={phoneInputRef}
                  type="text"
                  value={formData.identifier}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setFormData({ ...formData, identifier: val });
                    if (errors.identifier) {
                      setErrors(prev => ({ ...prev, identifier: '' }));
                    }
                  }}
                  placeholder="Enter 10-digit phone number"
                  className="w-full px-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg text-white"
                  required
                />
                {errors.identifier && (
                  <p className="mt-1 text-xs text-red-400">{errors.identifier}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={!isPhoneValid}
                className={`w-full font-semibold py-2 px-4 rounded-lg transition-all ${
                  isPhoneValid
                    ? 'bg-lime-400 text-[#181f10] hover:bg-lime-300'
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                }`}
              >
                Request OTP
              </button>
            </>
          ) : (
            /* Step 2 — OTP entry */
            <>
              <div className="text-sm text-gray-300 mb-2">
                OTP sent to +91 {formatPhoneNumber(formData.identifier)}{' '}
                <button
                  type="button"
                  onClick={handleChangePhone}
                  className="text-lime-400 hover:underline font-medium ml-1"
                >
                  Change
                </button>
              </div>

              <div className="otp-input-wrapper">
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={formData.otp}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setFormData({ ...formData, otp: val });
                    if (errors.otp) {
                      setErrors(prev => ({ ...prev, otp: '' }));
                    }
                  }}
                  className="otp-input"
                  required
                  autoFocus
                />
              </div>
              {errors.otp && (
                <p className="mt-1 text-xs text-red-400 text-center">{errors.otp}</p>
              )}

              <div className="text-sm text-gray-400 mt-2 text-center">
                {countdown > 0 ? (
                  <span>Didn't get a code? Resend in {formatTime(countdown)}</span>
                ) : (
                  <span>
                    Didn't get a code?{' '}
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      className="text-lime-400 hover:underline font-medium"
                    >
                      Resend OTP
                    </button>
                  </span>
                )}
              </div>

              <div className="flex items-center space-x-2 mt-4 mb-6">
                <input
                  type="checkbox"
                  id="rememberMeOtp"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 text-lime-400 bg-[#222] border-[#2b2b2b] rounded focus:ring-lime-400 cursor-pointer"
                />
                <label htmlFor="rememberMeOtp" className="text-sm text-gray-400 cursor-pointer select-none">
                  Remember me
                </label>
              </div>

              <button
                type="submit"
                className="w-full bg-lime-400 hover:bg-lime-300 text-[#181f10] font-semibold py-2 px-4 rounded-lg"
              >
                Verify
              </button>
            </>
          )}
        </>
      )}

      {/* Messages */}
      {message.text && (
        <div className={`text-sm text-center ${message.type === 'success' ? 'text-yellow-400' : 'text-red-400'}`}>
          {message.text}
        </div>
      )}
    </form>
  );
};


// Signup Form Component (Multi-step)
const SignupForm = ({ setIsLogin, setMessage, message }) => {

  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    phoneNumber: '',
    email: '',
    gender: '',
    password: '',
    accountType: 'member', // 'member' or 'gym-owner'
    gymName: '',
    location: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const phoneRegex = /^[6-9]\d{9}$/; // Indian 10-digit mobile starting with 6-9[web:25][web:27]
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // simple email format[web:26][web:31]
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };


  const validate = () => {
    const newErrors = {};

    // Phone (required)
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!phoneRegex.test(formData.phoneNumber.trim())) {
      newErrors.phoneNumber = 'Enter a valid 10-digit phone number';
    }

    // First name (required)
    if (!formData.firstname.trim()) {
      newErrors.firstname = 'First name is required';
    } else if (formData.firstname.trim().length < 2) {
      newErrors.firstname = 'First name must be at least 2 characters';
    }

    // Last name (required)
    if (!formData.lastname.trim()) {
      newErrors.lastname = 'Last name is required';
    } else if (formData.lastname.trim().length < 2) {
      newErrors.lastname = 'Last name must be at least 2 characters';
    }

    // Gender (optional) — no strict rule other than using the select

    // Email (optional, but if present must be valid)
    if (formData.email.trim()) {
      if (!emailRegex.test(formData.email.trim())) {
        newErrors.email = 'Enter a valid email address';
      }
    }

    // Password (required + strong)
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!passwordRegex.test(formData.password)) {
      newErrors.password = 'Password must be 8+ chars with upper, lower, number, special';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    if (!validate()) return;

    const payload = new FormData();
    payload.append('email', formData.email);
    payload.append('phoneNumber', formData.phoneNumber);
    payload.append('firstName', formData.firstname);
    payload.append('lastName', formData.lastname);
    payload.append('gender', formData.gender);
    payload.append('role', 'User');
    payload.append('password', formData.password);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register/${'SReg'}`, {
        method: "POST",
        body: payload
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Registration failed:", errorData);

        if (errorData && errorData.message === 'U201X0') {
          setMessage({ type: 'error', text: 'User already Exists!!' });
        } else {
          setMessage({ type: 'error', text: 'Registration failed' });
        }

      } else {
        await response.json();
        setMessage({ type: 'success', text: 'Registration successful. Please login.' });
        // Optionally redirect to login
        setIsLogin(true);

      }
    } catch (error) {
      console.error("Error during registration:", error);
      setMessage({ type: 'error', text: 'Error during registration.' });
    }

  };



  return (
    <form onSubmit={handleSubmit} className="space-y-4">

      <div align="left">
        <label htmlFor="phonenumber" className="block text-sm font-medium text-gray-300 mb-1">
          Phone Number *
        </label>
        <input
          type="string"
          id="phonenumber"
          maxLength={10}
          value={formData.phoneNumber}
          onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
          className="w-full px-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg text-white focus:ring-2 focus:ring-lime-400 focus:border-transparent"
          required
        />
        {errors.phoneNumber && (<p className="mt-1 text-xs text-red-400">{errors.phoneNumber}</p>
        )}
      </div>


      <div align="left">
        <label htmlFor="firstname" className="block text-sm font-medium text-gray-300 mb-1">
          First Name *
        </label>
        <input
          type="text"
          id="firstname"
          value={formData.firstname}
          onChange={(e) => setFormData({ ...formData, firstname: e.target.value })}
          className="w-full px-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg text-white focus:ring-2 focus:ring-lime-400 focus:border-transparent"
          required
        />
        {errors.firstname && (<p className="mt-1 text-xs text-red-400">{errors.firstname}</p>)}
      </div>
      <div align="left">
        <label htmlFor="lastname" className="block text-sm font-medium text-gray-300 mb-1">
          Last Name *
        </label>
        <input
          type="text"
          id="lastname"
          value={formData.lastname}
          onChange={(e) => setFormData({ ...formData, lastname: e.target.value })}
          className="w-full px-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg text-white focus:ring-2 focus:ring-lime-400 focus:border-transparent"
          required
        />
        {errors.lastname && (<p className="mt-1 text-xs text-red-400">{errors.lastname}</p>)}
      </div>

      <div align="left">
        <label htmlFor="gender" className="block text-sm font-medium text-gray-300 mb-1">Gender</label>
        <div className="relative">
          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />

          <select
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            className="w-full pl-10 pr-4 py-2 bg-[#222] border border-[#2b2b2b] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-lime-400 appearance-none"
          >
            <option value=""> --Select Gender--</option>
            <option value="Male">  &nbsp; ♂  Male</option>
            <option value="Female"> &nbsp; ♀  Female</option>
            <option value="Others"> &nbsp; ⚧  Others</option>

          </select>
          <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white" />
        </div>
      </div>

      <div align="left">
        <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
          Email
        </label>
        <input
          type="email"
          id="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="w-full px-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg text-white focus:ring-2 focus:ring-lime-400 focus:border-transparent"
        />
      </div>

      <div className="relative" align="left">
        <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
          Password *
        </label>
        <input
          type={showPassword ? "text" : "password"}
          id="password"
          value={formData.password}
          onChange={e => setFormData({ ...formData, password: e.target.value })}
          className="w-full px-4 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg text-white focus:ring-2 focus:ring-lime-400 focus:border-transparent pr-10"
          required
          minLength="8"
        />
        {errors.password && (<p className="mt-1 text-xs text-red-400">{errors.password}</p>)}
        {formData.password && (
          <button
            type="button"
            className="absolute right-3 top-[35%]   transform -translate-y-1/2 text-gray-400 hover:text-gray-200 focus:outline-none"
            onClick={() => setShowPassword(show => !show)}
            tabIndex={-1}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        )}
        {/* <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p> */}
        <p className="text-xs text-gray-500 mt-1">
          Password must contain:
          Minimum 8 characters
          At least one uppercase letter (A–Z)
          At least one lowercase letter (a–z)
          At least one number (0–9)
          At least one special character (e.g., !@#$%^&*)
        </p>
      </div>

      <button type="submit" className="bg-lime-400 hover:bg-lime-300 text-[#181f10] font-semibold py-2 px-6 rounded-lg transition">
        Complete Registration
      </button>

      {message.text && (
        <div className={`text-sm text-center ${message.type === 'success' ? 'text-yellow-400' : 'text-red-400'}`}>
          {message.text}
        </div>
      )}
    </form>
  );
};

export default AuthModal;